'use strict';
import { createRoot } from 'react-dom/client';
import Routes from './components/Routes.jsx'
const swea = require('swea');

const root = createRoot(document.getElementById('main'));

function startApplication() {
  swea.i18nReady.then(() => {
    // Change language if desired
    const lang = swea.util.getQueryParameters().lang;
    if (lang !== undefined) {
      swea.i18n.changeLanguage(lang, () => {
        root.render(Routes);
      });
    }
    else {
      // No language specified
      root.render(Routes);
    }
  });
}

/**
 * Redirects to a given project in the Spotscale portal.
 */
function redirectToPortal(portal, guid) {
  window.location.href = portal + '/projects/' + guid;
}

/**
 * Reads portal address from the settings file if found
 * @param { string } baseUrl The base URL to the model storage account.
 * @returns { Promise } A promise resolving with an object containing portalAddress and approvedAddresses of other approved addresses, or rejecting if settings aren't found.
 */
function readSettings(baseUrl) {
  return new Promise((resolve, reject) => {
    const settingsUrl = baseUrl + 'settings/settings.json?' + Math.random();
    swea.editor.HTTP.get(settingsUrl).then((res) => {
      resolve(res.data);
    }).catch((err) => {
      reject(err);
    });
  });
}

/**
 * Reports whether a given portal address is valid or not.
 * @param { string } messagePortalAddress The portal address from the postMessage reply.
 * @param { string } portalAddress The portal address from the settings file.
 * @param { string[] } approvedAddresses An array of approved portal addresses, from the settings file.
 * @returns { boolean } True if valid.
 * @private
 */
function _validPortalAddress(messagePortalAddress, portalAddress, approvedAddresses) {
  if (messagePortalAddress === portalAddress) {
    return true;
  }
  return (approvedAddresses !== undefined && approvedAddresses.indexOf(messagePortalAddress) > -1);
}

/**
 * Initializes the application by rendering the Routes in the main div.
 */
function initializeApplication() {
  const projectGuid = swea.util.getQueryParameters().project;
  const modelUrl = swea.util.getQueryParameters().model;

  let communicationId = undefined;
  const communication = swea.util.getQueryParameters().communication;
  if (communication !== undefined) {
    communicationId = parseInt(communication);
  }
  
  if (modelUrl !== undefined) {
    const baseUrl = swea.util.getBaseUrl(modelUrl);
    if (communicationId !== undefined && projectGuid !== undefined) {
      // Listen for message from the Spotscale cloud portal
      window.addEventListener('message', (evt) => {
        const id = evt.data.id;
        const command = evt.data.command;
        const messagePortalAddress = evt.data.value;
        const projectName = evt.data.projectName;
        const userName = evt.data.userName;
        const organizations = evt.data.organizations;
        if (id === communicationId && command === 'portal' && messagePortalAddress !== undefined) {
          // We got an iframe message object with a url key, check that it's the same as the portal URL
          // read from the settings file in the model storage
          readSettings(baseUrl).then((data) => {
            if (_validPortalAddress(messagePortalAddress, data.portal, data.approved)) {
              // We got a match, set portal address and start the application!
              swea.configure({
                cloudServer: messagePortalAddress,
                projectName: projectName,
                userName: userName,
                organizations: organizations
              });
              startApplication();
            }
            else {
              // URLs don't match, redirect to portal
              redirectToPortal(data.portal, projectGuid);
            }
          }).catch((err) => {
            if (err.status === 404) {
              // No settings file, stand-alone viewer
              startApplication();
            }
            else {
              alert('Failed reading portal address.');
            }
          });
        }
        else {
          // No URL parsed from the model URL, or incorrect iframe message - not a portal model
          startApplication();
        }
      });
    }
    else {
      // Stand-alone viewer started, just start application since if one gets hold of the direct link one has been logged in
      startApplication();
    }
  }
  else {
    // Not even a model defined, start viewer in case it will be fetched in some other way
    startApplication();
  }
}

// Make sure that browser differences do not affect the loading of the application.
if ((
    'complete' === document.readyState ||
    'loaded' === document.readyState ||
    'interactive' === document.readyState) &&
    document.body
) {
  initializeApplication();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    initializeApplication();
  }, false);
}
